<template>
  <pop-up-layout 
    :title="$t('reservas.contrato.representantes')"
    @volver="$parent.hide()"
    @aceptar="aceptar"
    :aceptar="$t('reservas.contrato.continuar')"
  >
    <v-form v-model="valid" ref="form">
      <v-layout align-center :column="$vuetify.breakpoint.smAndDown">
        <v-flex xs12 sm6 px-2>
          <v-select
            :label="$t('reservas.contrato.plantillas')"
            v-model="mPlantilla"
            :items="plantillas"
            item-text="nombreIdioma"
            item-value="plantilla.id"
            :rules="[rules.required]"
          />
        </v-flex>
        <v-btn
          :disabled="mPlantilla == null"
          @click.stop
          :href="$plantillaDocumento(mPlantilla)" target="_blank"
          color="primary"
        >
          {{$t('reservas.contrato.verDoc')}}
        </v-btn>
      </v-layout>
      <v-layout wrap v-if="!ocultar">
        <v-flex xs12 sm6 px-2>
          <v-select
            :label="$t('reservas.contrato.tipoFirma')"
            v-model="mTipo"
            :items="tipoItems"
            :rules="[rules.required]"
          />
        </v-flex>
        <v-flex xs12 sm6 px-2>
          <v-checkbox
            :label="$t('reservas.contrato.claveSMS')"
            v-model="mOtp"
            color="primary"
            :rules="[rules.required]"
          />
        </v-flex>
      </v-layout>
      <v-layout px-2 mb-2>
        <v-flex xs12>
          <v-data-table
            select-all
            v-model="selected"
            :headers="headers"
            :items="filteredItems"
            :expand="$vuetify.breakpoint.smAndDown"
            :pagination.sync="pagination"
            hide-actions
          >
          <template slot="headers" slot-scope="props">
              <tr>
                <th :class="{tableSmallPadding: $vuetify.breakpoint.xsOnly}">
                  <v-checkbox
                    :input-value="props.all"
                    :indeterminate="props.indeterminate"
                    color="primary"
                    hide-details
                    @click.stop="toggleAll"
                  />
                </th>
                <th
                  v-for="header in props.headers"
                  :key="header.text"
                  :class="[
                    header.align ? 'text-xs-'+header.align : '',
                    header.class ? header.class : '', 
                    'column sortable', 
                    pagination.descending ? 'desc' : 'asc', 
                    header.value === pagination.sortBy ? 'active' : ''
                  ]"
                  @click="changeSort(header.value)"
                >
                  {{ header.text }}
                  <v-icon small>arrow_upward</v-icon>
                </th>
              </tr>
            </template>
            <template v-slot:items="props">
              <tr :class="{pointer: $vuetify.breakpoint.smAndDown}" @click="props.expanded = !props.expanded">
                <td @click.stop :class="{tableSmallPadding: $vuetify.breakpoint.xsOnly}">
                  <v-checkbox
                    :key="props.item.id"
                    v-model="props.selected"
                    color="primary"
                    hide-details
                  />
                </td>
                <td>
                  <v-layout row>
                    {{ props.item.nombre }}
                    <v-spacer/>
                    <v-icon
                      v-if="$vuetify.breakpoint.smAndDown"
                      :style="props.expanded ? 'transform: rotate(-180deg)' : ''"
                    > 
                      keyboard_arrow_down 
                    </v-icon>
                  </v-layout>
                </td>
                <td class="hidden-sm-and-down text-xs-center">{{ props.item.firma }}</td>
                <td class="hidden-xs-only" :class="{
                  'text-xs-center': !$vuetify.breakpoint.smAndDown,
                  'text-xs-right': $vuetify.breakpoint.smAndDown,
                }">{{ props.item.email }}</td>
                <td class="hidden-sm-and-down text-xs-right">{{ props.item.telefono }}</td>
              </tr>
            </template>
            <template 
              v-if="$vuetify.breakpoint.smAndDown" 
              v-slot:expand="props"
            >
              <v-list dense>
                <v-list-tile>
                  <v-list-tile-action>
                    <v-tooltip bottom>
                      <v-icon color="primary" slot="activator"> mdi-account </v-icon>
                      <span>{{$t('reservas.contrato.nombre')}}</span>
                    </v-tooltip>
                  </v-list-tile-action>
                  <v-list-tile-content>
                    <v-layout row align-center>
                      <v-flex>
                        {{props.item.nombre}}
                      </v-flex>
                    </v-layout>
                  </v-list-tile-content>
                </v-list-tile>
                <v-list-tile>
                  <v-list-tile-action>
                    <v-tooltip bottom>
                      <v-icon color="primary" slot="activator"> mdi-signature-freehand </v-icon>
                      <span>{{$t('reservas.contrato.disponeFirma')}}</span>
                    </v-tooltip>
                  </v-list-tile-action>
                  <v-list-tile-content>
                    <v-layout row align-center>
                      <v-flex>
                        {{props.item.firma}}
                      </v-flex>
                    </v-layout>
                  </v-list-tile-content>
                </v-list-tile>
                <v-list-tile>
                  <v-list-tile-action>
                    <v-tooltip bottom>
                      <v-icon color="primary" slot="activator"> mdi-email </v-icon>
                      <span>{{$t('reservas.contrato.email')}}</span>
                    </v-tooltip>
                  </v-list-tile-action>
                  <v-list-tile-content>
                    <v-layout row align-center>
                      <v-flex>
                        {{ props.item.email }}
                      </v-flex>
                    </v-layout>
                  </v-list-tile-content>
                </v-list-tile>
                <v-list-tile>
                  <v-list-tile-action>
                    <v-tooltip bottom>
                      <v-icon color="primary" slot="activator">mdi-phone</v-icon>
                      <span>{{$t('reservas.contrato.telefono')}}</span>
                    </v-tooltip>
                  </v-list-tile-action>
                  <v-list-tile-content>
                    <v-layout row align-center>
                      <v-flex> 
                        {{ props.item.telefono }}
                      </v-flex>
                    </v-layout>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list>
            </template>
          </v-data-table>
        </v-flex>
      </v-layout>
    </v-form>
    <v-fade-transition>
      <custom-dialog v-if="mostrarConfirmar" v-model="mostrarConfirmar" :noCerrar="true">
        <ContratoConfirmarFirmantes
          :representantes="representantesFirma"
          :pantalla="mTipo === 'url'"
          @aceptar="aceptar(true, false)"
        />
      </custom-dialog>
    </v-fade-transition>
  </pop-up-layout>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import rulesMixin from '@/mixins/rules.js'
import ContratoConfirmarFirmantes from './ContratoConfirmarFirmantes'
export default {
  mixins: [rulesMixin],
  components: {ContratoConfirmarFirmantes},
  props: {
    otp: {
      type: Boolean,
      required: true
    },
    representantes: {
      type: Array,
      required: true
    },
    plantilla: {
      type: Number
    },
    tipo: {
      type: String,
      required: true,
      validator: function (value) {
        return ['url', 'email'].some(x => x === value)
      }
    },
    ocultar: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      expanded: null,
      selected: [],
      mPlantilla: null,
      mOtp: false,
      mTipo: 'url',
      pagination: {
        sortBy: 'nombre',
        rowsPerPage: -1,
        descending: false
      },
      mostrarAlerta: false,
      mostrarConfirmar: false,
      inicializado: false,
      valid: true
    }
  },
  async created () {
    this.parseFirmantes()
    this.mOtp = this.otp
    this.mTipo = this.tipo
    this.mPlantilla = this.plantillas.length === 0 ? null : this.plantilla
    await this.$nextTick()
    this.inicializado = true
  },
  computed: {
    ...mapGetters({
      reserva: 'reservas/_id/getActual',
      getAllRepresentantes: 'promocionesRepresentantes/getAll',
      getPromocionPlantillas: 'promocionesPlantillas/getAll'
    }),
    tipoItems () {
      return [
        {
          text: this.$t('reservas.contrato.porCorreo'),
          value: 'email'
        },
        {
          text: this.$t('reservas.contrato.enPantalla'),
          value: 'url'
        }
      ]
    },
    plantillas () {
      return this.getPromocionPlantillas.filter(x => x.ambito === this.reserva.cod_tipo_operacion).map(x => ({
        ...x,
        nombreIdioma: x.plantilla.nombre + ' (' + this.$t('general.idiomas.' + x.plantilla.idioma.toLowerCase()) + ')'
      }))
    },
    headers () {
      return [
        {
          text: this.$t('reservas.contrato.nombre'),
          value: 'nombre',
          align: 'left',
          sortable: true
        },
        {
          text: this.$t('reservas.contrato.disponeFirma'),
          value: 'firma',
          align: 'center',
          sortable: true,
          class: 'hidden-sm-and-down'
        },
        {
          text: this.$t('reservas.contrato.email'),
          value: 'email',
          align: !this.$vuetify.breakpoint.smAndDown ? 'center' : 'right',
          sortable: true,
          class: 'hidden-xs-only'
        },
        {
          text: this.$t('reservas.contrato.telefono'),
          value: 'telefono',
          align: 'right',
          sortable: true,
          class: 'hidden-sm-and-down'
        }
      ]
    },
    filteredItems () {
      return this.getAllRepresentantes.map(f => {
        return {
          id: f.id,
          nombre: f.representante.nombreApellido,
          email: f.email,
          telefono: (this.esComercial || this.esAdministrador) ? f.movil : ('********' + f.movil.slice('-3')),
          esPorDefecto: f.esPorDefecto,
          firma: f.representante.documento_id != null ? this.$t('reservas.resumenAlquiler.si') : this.$t('reservas.resumenAlquiler.no')
        }
      })
    },
    textoAlerta () {
      let aux = this.getAllRepresentantes.filter(f => this.selected.some(n => n.id === f.id)).filter(x => x.representante.documento_id == null).map(x => x.representante.nombreApellido)
      let texto = ''
      if (aux.length <= 2) {
        texto = aux.join(' y ')
      } else {
        texto = [aux.slice(0, -1).join(', '), aux[aux.length - 1]].join(' y ')
      }
      if (aux.length <= 1) {
        return this.$t('reservas.contrato.representantePresente', {texto: texto})
      } else {
        return this.$t('reservas.contrato.representantesPresente', {texto: texto})
      }
    },
    representantesFirma () {
      return this.getAllRepresentantes.filter(f => this.selected.some(n => n.id === f.id)).filter(x => x.representante.documento_id == null)
    }  
  },
  methods: {
    ...mapActions({
      addAlert: 'alerts/add'
    }),
    toggleAll () {
      if (this.selected.length) this.selected = []
      else this.selected = this.selected = this.getAllRepresentantes.slice()
    },
    changeSort (column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending
      } else {
        this.pagination.sortBy = column
        this.pagination.descending = false
      }
    },
    parseFirmantes () {
      this.selected = this.filteredItems.filter(f => this.representantes.some(n => n.id === f.id))
    },
    aceptar (confirmados = false) {
      this.$refs.form.validate()
      if (!this.valid) {
        this.addAlert({texto: this.$t('general.misc.revisaCamposRojo'), color: 'error'})
        return confirmados
      }
      let aux = this.getAllRepresentantes.filter(f => this.selected.some(n => n.id === f.id))
      if (!confirmados && this.representantesFirma.length > 0) {
        this.mostrarConfirmar = true
        return
      }
      this.$emit('update:plantilla', this.mPlantilla)
      this.$emit('update:representantes', aux)
      this.$emit('update:otp', this.mOtp)
      this.$emit('update:tipo', this.mTipo)
      this.$emit('aceptar')
    },
    cambiado () {
      if (this.inicializado) {
        this.$emit('cambiado', true)
      }
    },
    aceptarAlerta (aceptar) {
      this.mostrarAlerta = false
      if (aceptar) {
        this.aceptar(true, true)
      }
    }
  },
  watch: {
    selected () {
      this.cambiado()
    },
    mOtp () {
      this.cambiado()
    },
    mTipo () {
      this.cambiado()
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/reservas/contratos/ContratoElegirFirmantes.vue